import { supabase } from '../lib/supabase';
import { statementImportsService } from './statementImportsService';

class BankingService {
  constructor() {
    this.tableName = 'bank_accounts';
    this.transactionsTableName = 'bank_transactions';
    this.statementsTableName = 'statement_imports';
  }

  // Enhanced getCurrentUserOrganization with better error handling and fallback
  async getCurrentUserOrganization() {
    try {
      // First, try to get the session which is more reliable
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw new Error('Authentication session error: ' + sessionError.message);
      }
      
      if (!session || !session?.user) {
        throw new Error('Not authenticated - no active session');
      }
      
      const user = session?.user;
      
      // Get organization membership with better error handling
      const { data: orgMember, error } = await supabase
        ?.from('organization_members')
        ?.select('organization_id')
        ?.eq('user_id', user?.id)
        ?.maybeSingle(); // Use maybeSingle to handle cases where no organization exists

      if (error) {
        console.error('Organization membership error:', error);
        throw new Error('Failed to fetch organization membership: ' + error.message);
      }
      
      if (!orgMember) {
        throw new Error('User is not a member of any organization');
      }

      return { user, organizationId: orgMember?.organization_id };
    } catch (error) {
      console.error('getCurrentUserOrganization failed:', error);
      throw error;
    }
  }

  // Add customer methods for banking transactions
  async getAllCustomers() {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('customers')?.select(`
          id,
          customer_name,
          customer_number,
          email,
          status,
          total_outstanding
        `)?.eq('organization_id', organizationId)?.eq('status', 'active')?.order('customer_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  // Get customer invoices for autofill
  async getCustomerInvoices(customerId) {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('invoices')?.select(`
          id,
          invoice_number,
          total_amount,
          amount_paid,
          balance_due,
          due_date,
          status,
          issue_date
        `)?.eq('organization_id', organizationId)?.eq('customer_id', customerId)?.in('status', ['sent', 'viewed', 'partially_paid', 'overdue'])?.order('issue_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching customer invoices:', error);
      throw error;
    }
  }

  // Get invoice details for autofill
  async getInvoiceDetails(invoiceId) {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('invoices')?.select(`
          id,
          invoice_number,
          total_amount,
          amount_paid,
          balance_due,
          due_date,
          status,
          issue_date,
          customer:customers!invoices_customer_id_fkey(
            id,
            customer_name,
            customer_number,
            email
          )
        `)?.eq('id', invoiceId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      throw error;
    }
  }

  // Add vendor methods for banking transactions
  async getAllVendors() {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('vendors')?.select(`
          id,
          vendor_name,
          vendor_number,
          email,
          status
        `)?.eq('organization_id', organizationId)?.eq('status', 'active')?.order('vendor_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  }

  // Get vendor bills for autofill
  async getVendorBills(vendorId) {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('bills')?.select(`
          id,
          bill_number,
          total_amount,
          amount_paid,
          balance_due,
          due_date,
          status,
          issue_date
        `)?.eq('organization_id', organizationId)?.eq('vendor_id', vendorId)?.in('status', ['pending_approval', 'approved', 'overdue'])?.order('issue_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vendor bills:', error);
      throw error;
    }
  }

  // Get bill details for autofill
  async getBillDetails(billId) {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.from('bills')?.select(`
          id,
          bill_number,
          total_amount,
          amount_paid,
          balance_due,
          due_date,
          status,
          issue_date,
          vendor:vendors!bills_vendor_id_fkey(
            id,
            vendor_name,
            vendor_number,
            email
          )
        `)?.eq('id', billId)?.eq('organization_id', organizationId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching bill details:', error);
      throw error;
    }
  }

  // Convert database snake_case to React camelCase
  convertToCamelCase(bankAccount) {
    return {
      id: bankAccount?.id,
      organizationId: bankAccount?.organization_id,
      createdBy: bankAccount?.created_by,
      accountType: bankAccount?.account_type,
      accountName: bankAccount?.account_name,
      accountCode: bankAccount?.account_code,
      currency: bankAccount?.currency,
      accountNumber: bankAccount?.account_number,
      bankName: bankAccount?.bank_name,
      bankIdentifierCode: bankAccount?.bank_identifier_code,
      description: bankAccount?.description,
      isPrimary: bankAccount?.is_primary,
      currentBalance: parseFloat(bankAccount?.current_balance || 0),
      availableBalance: parseFloat(bankAccount?.available_balance || 0),
      status: bankAccount?.status,
      feedEnabled: bankAccount?.feed_enabled,
      feedStatus: bankAccount?.feed_status,
      lastSyncAt: bankAccount?.last_sync_at,
      createdAt: bankAccount?.created_at,
      updatedAt: bankAccount?.updated_at
    };
  }

  // Convert React camelCase to database snake_case
  convertToSnakeCase(bankAccount) {
    return {
      account_type: bankAccount?.accountType,
      account_name: bankAccount?.accountName,
      account_code: bankAccount?.accountCode,
      currency: bankAccount?.currency,
      account_number: bankAccount?.accountNumber,
      bank_name: bankAccount?.bankName,
      bank_identifier_code: bankAccount?.bankIdentifierCode,
      description: bankAccount?.description,
      is_primary: bankAccount?.isPrimary,
      current_balance: bankAccount?.currentBalance || 0,
      available_balance: bankAccount?.availableBalance || 0,
      status: bankAccount?.status || 'active',
      feed_enabled: bankAccount?.feedEnabled || false,
      feed_status: bankAccount?.feedStatus || 'inactive'
    };
  }

  // Convert transaction data
  convertTransactionToCamelCase(transaction) {
    return {
      id: transaction?.id,
      bankAccountId: transaction?.bank_account_id,
      organizationId: transaction?.organization_id,
      createdBy: transaction?.created_by,
      transactionDate: transaction?.transaction_date,
      description: transaction?.description,
      amount: parseFloat(transaction?.amount || 0),
      category: transaction?.category,
      referenceNumber: transaction?.reference_number,
      reconciliationStatus: transaction?.reconciliation_status,
      reconciledBy: transaction?.reconciled_by,
      reconciledAt: transaction?.reconciled_at,
      createdAt: transaction?.created_at,
      updatedAt: transaction?.updated_at,
      // Add support for AI categorization fields
      categoryConfidence: transaction?.category_confidence,
      aiSuggested: transaction?.ai_suggested,
      accountId: transaction?.account_id
    };
  }

  // Get all bank accounts for current user's organization
  async getAllBankAccounts() {
    const { organizationId } = await this.getCurrentUserOrganization();

    const { data, error } = await supabase?.from(this.tableName)?.select('*')?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(account => this.convertToCamelCase(account));
  }

  // Get a specific bank account by ID
  async getBankAccountById(accountId) {
    const { organizationId } = await this.getCurrentUserOrganization();

    const { data, error } = await supabase?.from(this.tableName)?.select('*')?.eq('id', accountId)?.eq('organization_id', organizationId)?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  }

  // Create a new bank account
  async createBankAccount(bankAccountData) {
    const { user, organizationId } = await this.getCurrentUserOrganization();

    // Generate account code if not provided
    if (!bankAccountData?.accountCode) {
      const { data: codeResult } = await supabase?.rpc('generate_bank_account_number', {
        organization_uuid: organizationId
      });
      bankAccountData.accountCode = codeResult || 'BANK001';
    }

    // If this is set as primary, unset other primary accounts
    if (bankAccountData?.isPrimary) {
      await supabase?.from(this.tableName)?.update({ is_primary: false })?.eq('organization_id', organizationId)?.eq('is_primary', true);
    }

    const dbData = {
      ...this.convertToSnakeCase(bankAccountData),
      organization_id: organizationId,
      created_by: user?.id
    };

    const { data, error } = await supabase?.from(this.tableName)?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  }

  // Update a bank account
  async updateBankAccount(accountId, updateData) {
    const { organizationId } = await this.getCurrentUserOrganization();

    // If this is set as primary, unset other primary accounts
    if (updateData?.isPrimary) {
      await supabase?.from(this.tableName)?.update({ is_primary: false })?.eq('organization_id', organizationId)?.eq('is_primary', true)?.neq('id', accountId);
    }

    const dbData = this.convertToSnakeCase(updateData);

    const { data, error } = await supabase?.from(this.tableName)?.update(dbData)?.eq('id', accountId)?.eq('organization_id', organizationId)?.select()?.single();

    if (error) throw error;

    return this.convertToCamelCase(data);
  }

  // Delete a bank account
  async deleteBankAccount(accountId) {
    const { organizationId } = await this.getCurrentUserOrganization();

    const { error } = await supabase?.from(this.tableName)?.delete()?.eq('id', accountId)?.eq('organization_id', organizationId);

    if (error) throw error;

    return { success: true };
  }

  // Get transactions for a specific bank account
  async getBankAccountTransactions(accountId, filters = {}) {
    const { organizationId } = await this.getCurrentUserOrganization();

    let query = supabase?.from(this.transactionsTableName)?.select('*')?.eq('organization_id', organizationId);

    if (accountId && accountId !== 'all') {
      query = query?.eq('bank_account_id', accountId);
    }

    // Apply filters
    if (filters?.dateFrom) {
      query = query?.gte('transaction_date', filters?.dateFrom);
    }
    if (filters?.dateTo) {
      query = query?.lte('transaction_date', filters?.dateTo);
    }
    if (filters?.category && filters?.category !== 'all') {
      query = query?.eq('category', filters?.category);
    }
    if (filters?.reconciliationStatus && filters?.reconciliationStatus !== 'all') {
      query = query?.eq('reconciliation_status', filters?.reconciliationStatus);
    }
    if (filters?.type && filters?.type !== 'all') {
      if (filters?.type === 'income') {
        query = query?.gt('amount', 0);
      } else if (filters?.type === 'expense') {
        query = query?.lt('amount', 0);
      }
    }
    if (filters?.search) {
      query = query?.ilike('description', `%${filters?.search}%`);
    }

    query = query?.order('transaction_date', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(transaction => this.convertTransactionToCamelCase(transaction));
  }

  // Create a new transaction
  async createTransaction(transactionData) {
    const { user, organizationId } = await this.getCurrentUserOrganization();

    const dbData = {
      bank_account_id: transactionData?.bankAccountId,
      organization_id: organizationId,
      created_by: user?.id,
      transaction_date: transactionData?.transactionDate || new Date()?.toISOString()?.split('T')?.[0],
      description: transactionData?.description,
      amount: transactionData?.amount,
      category: transactionData?.category,
      reference_number: transactionData?.referenceNumber,
      reconciliation_status: transactionData?.reconciliationStatus || 'unreconciled'
    };

    const { data, error } = await supabase?.from(this.transactionsTableName)?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return this.convertTransactionToCamelCase(data);
  }

  // Enhanced Update transaction method with better error handling and AI field support
  async updateTransaction(transactionId, updateData) {
    const { organizationId } = await this.getCurrentUserOrganization();

    // Validate that the transaction exists and belongs to the organization
    const existingTransaction = await this.getTransactionById(transactionId);
    if (!existingTransaction) {
      throw new Error('Transaction not found');
    }

    // Convert to snake_case for database
    const dbData = {
      transaction_date: updateData?.transactionDate,
      description: updateData?.description,
      amount: updateData?.amount,
      category: updateData?.category,
      reference_number: updateData?.referenceNumber,
      reconciliation_status: updateData?.reconciliationStatus,
      // Add AI categorization fields support
      category_confidence: updateData?.categoryConfidence,
      ai_suggested: updateData?.aiSuggested,
      account_id: updateData?.accountId
    };

    // Remove undefined values
    Object.keys(dbData)?.forEach(key => {
      if (dbData?.[key] === undefined) {
        delete dbData?.[key];
      }
    });

    const { data, error } = await supabase?.from(this.transactionsTableName)?.update(dbData)?.eq('id', transactionId)?.eq('organization_id', organizationId)?.select()?.single();

    if (error) throw error;

    return this.convertTransactionToCamelCase(data);
  }

  // Add method to get single transaction by ID
  async getTransactionById(transactionId) {
    const { organizationId } = await this.getCurrentUserOrganization();

    const { data, error } = await supabase?.from(this.transactionsTableName)?.select('*')?.eq('id', transactionId)?.eq('organization_id', organizationId)?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        return null; // Transaction not found
      }
      throw error;
    }

    return this.convertTransactionToCamelCase(data);
  }

  // Delete transaction
  async deleteTransaction(transactionId) {
    const { organizationId } = await this.getCurrentUserOrganization();

    const { error } = await supabase?.from(this.transactionsTableName)?.delete()?.eq('id', transactionId)?.eq('organization_id', organizationId);

    if (error) throw error;

    return { success: true };
  }

  // Update transaction reconciliation status
  async updateTransactionReconciliation(transactionId, status) {
    const { user, organizationId } = await this.getCurrentUserOrganization();

    const updateData = {
      reconciliation_status: status,
      reconciled_by: status === 'reconciled' ? user?.id : null,
      reconciled_at: status === 'reconciled' ? new Date()?.toISOString() : null
    };

    const { data, error } = await supabase?.from(this.transactionsTableName)?.update(updateData)?.eq('id', transactionId)?.eq('organization_id', organizationId)?.select()?.single();

    if (error) throw error;

    return this.convertTransactionToCamelCase(data);
  }

  // Bulk update transaction reconciliation
  async bulkUpdateTransactionReconciliation(transactionIds, status) {
    const { user, organizationId } = await this.getCurrentUserOrganization();

    const updateData = {
      reconciliation_status: status,
      reconciled_by: status === 'reconciled' ? user?.id : null,
      reconciled_at: status === 'reconciled' ? new Date()?.toISOString() : null
    };

    const { data, error } = await supabase?.from(this.transactionsTableName)?.update(updateData)?.in('id', transactionIds)?.eq('organization_id', organizationId)?.select();

    if (error) throw error;

    return data?.map(transaction => this.convertTransactionToCamelCase(transaction));
  }

  // Enhanced sync account method for bank feeds
  async syncBankAccount(accountId) {
    const { organizationId } = await this.getCurrentUserOrganization();

    // Validate account exists
    const account = await this.getBankAccountById(accountId);
    if (!account) {
      throw new Error('Bank account not found');
    }

    // Update sync status to indicate sync in progress
    await this.updateBankAccount(accountId, {
      feedStatus: 'active',
      lastSyncAt: new Date()?.toISOString()
    });

    // In a real implementation, this would trigger external bank API calls
    // For now, we'll return success to indicate the sync process started
    return {
      success: true,
      message: 'Account sync initiated successfully',
      lastSyncAt: new Date()?.toISOString()
    };
  }

  // Add method to get transaction categories for better categorization
  getCommonCategories() {
    return [
      'Revenue',
      'Expense', 
      'Transfer',
      'Deposit',
      'Withdrawal',
      'Fee',
      'Interest',
      'Refund',
      'Payment',
      'Investment',
      'Loan',
      'Tax',
      'Insurance',
      'Utilities',
      'Office Supplies',
      'Travel',
      'Marketing',
      'Uncategorized'
    ];
  }

  // Get banking summary statistics
  async getBankingSummary() {
    const { organizationId } = await this.getCurrentUserOrganization();

    // Get all bank accounts
    const { data: accounts, error: accountsError } = await supabase?.from(this.tableName)?.select('current_balance')?.eq('organization_id', organizationId)?.eq('status', 'active');

    if (accountsError) throw accountsError;

    // Get transactions for this month
    const startOfMonth = new Date();
    startOfMonth?.setDate(1);
    const startOfMonthStr = startOfMonth?.toISOString()?.split('T')?.[0];

    const { data: transactions, error: transactionsError } = await supabase?.from(this.transactionsTableName)?.select('amount, reconciliation_status')?.eq('organization_id', organizationId)?.gte('transaction_date', startOfMonthStr);

    if (transactionsError) throw transactionsError;

    // Calculate summary
    const totalBalance = accounts?.reduce((sum, account) => sum + parseFloat(account?.current_balance || 0), 0);
    const totalIncome = transactions?.filter(t => parseFloat(t?.amount) > 0)?.reduce((sum, t) => sum + parseFloat(t?.amount), 0);
    const totalExpenses = transactions?.filter(t => parseFloat(t?.amount) < 0)?.reduce((sum, t) => sum + Math.abs(parseFloat(t?.amount)), 0);
    const unreconciledTransactions = transactions?.filter(t => t?.reconciliation_status === 'unreconciled')?.length;
    const pendingTransactions = transactions?.filter(t => t?.reconciliation_status === 'pending')?.length;

    return {
      totalBalance,
      totalIncome,
      totalExpenses,
      unreconciledTransactions,
      pendingTransactions,
      accountsCount: accounts?.length
    };
  }

  // Statement Import Methods

  // Get statement imports for organization
  async getStatementImports() {
    const { organizationId } = await this.getCurrentUserOrganization();
    return await statementImportsService?.getAll(organizationId);
  }

  // Upload and process bank statement file
  async uploadBankStatement(file, bankAccountId) {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();

      // Validate file type
      const allowedTypes = ['csv', 'excel', 'pdf', 'qif', 'ofx'];
      const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
      
      if (!allowedTypes?.includes(fileExtension)) {
        throw new Error('Unsupported file type. Please upload CSV, Excel, PDF, QIF, or OFX files.');
      }

      // Validate file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        throw new Error('File size too large. Maximum size is 10MB.');
      }

      // Upload file to storage
      const uploadResult = await statementImportsService?.uploadFile(file, organizationId);

      // Create statement import record
      const importRecord = await statementImportsService?.create({
        organizationId,
        bankAccountId,
        fileName: uploadResult?.fileName,
        filePath: uploadResult?.path,
        fileType: fileExtension,
        fileSize: uploadResult?.fileSize
      });

      // Start processing (in a real app, this would be a background job)
      this.processStatementImport(importRecord?.id, file, bankAccountId);

      return importRecord;
    } catch (error) {
      throw new Error('Failed to upload bank statement: ' + error?.message);
    }
  }

  // Process statement import (basic CSV processing for demo)
  async processStatementImport(importId, file, bankAccountId) {
    try {
      // Update status to processing
      await statementImportsService?.updateStatus(importId, 'processing');

      const { organizationId } = await this.getCurrentUserOrganization();

      // For CSV files, parse and create transactions
      if (file?.type === 'text/csv' || file?.name?.toLowerCase()?.endsWith('.csv')) {
        const text = await file?.text();
        const lines = text?.split('\n');
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());

        let importedCount = 0;
        let failedCount = 0;
        let startDate = null;
        let endDate = null;

        for (let i = 1; i < lines?.length; i++) {
          const line = lines?.[i]?.trim();
          if (!line) continue;

          try {
            const values = line?.split(',')?.map(v => v?.trim()?.replace(/"/g, ''));
            
            // Map common CSV column names to our fields
            const dateIndex = headers?.findIndex(h => 
              h?.includes('date') || h?.includes('transaction')
            );
            const descriptionIndex = headers?.findIndex(h => 
              h?.includes('description') || h?.includes('memo') || h?.includes('detail')
            );
            const amountIndex = headers?.findIndex(h => 
              h?.includes('amount') || h?.includes('debit') || h?.includes('credit')
            );

            if (dateIndex === -1 || descriptionIndex === -1 || amountIndex === -1) {
              throw new Error('Required columns not found');
            }

            const dateStr = values?.[dateIndex];
            const description = values?.[descriptionIndex];
            const amountStr = values?.[amountIndex]?.replace(/[,$]/g, '');

            // Parse date
            const transactionDate = new Date(dateStr);
            if (isNaN(transactionDate?.getTime())) {
              throw new Error(`Invalid date: ${dateStr}`);
            }

            // Parse amount
            const amount = parseFloat(amountStr);
            if (isNaN(amount)) {
              throw new Error(`Invalid amount: ${amountStr}`);
            }

            // Track date range
            if (!startDate || transactionDate < startDate) startDate = transactionDate;
            if (!endDate || transactionDate > endDate) endDate = transactionDate;

            // Create transaction
            await this.createTransaction({
              bankAccountId,
              transactionDate: transactionDate?.toISOString()?.split('T')?.[0],
              description: description || 'Imported transaction',
              amount,
              category: 'uncategorized',
              reconciliationStatus: 'unreconciled'
            });

            importedCount++;
          } catch (rowError) {
            // Log error to statement_import_errors table
            await supabase?.from('statement_import_errors')?.insert({
              statement_import_id: importId,
              row_number: i,
              error_type: 'processing_error',
              error_message: rowError?.message,
              raw_data: { row: line }
            });
            failedCount++;
          }
        }

        // Update import status
        await statementImportsService?.updateStatus(importId, 'completed', {
          totalTransactions: importedCount + failedCount,
          importedTransactions: importedCount,
          failedTransactions: failedCount,
          startDate: startDate?.toISOString()?.split('T')?.[0],
          endDate: endDate?.toISOString()?.split('T')?.[0]
        });

      } else {
        // For other file types, mark as completed but indicate manual processing needed
        await statementImportsService?.updateStatus(importId, 'completed', {
          errorMessage: 'File uploaded successfully. Manual processing may be required for non-CSV files.'
        });
      }

    } catch (error) {
      // Update status to failed
      await statementImportsService?.updateStatus(importId, 'failed', {
        errorMessage: error?.message
      });
      throw error;
    }
  }

  // Get statement import by ID
  async getStatementImportById(importId) {
    const { organizationId } = await this.getCurrentUserOrganization();
    const imports = await statementImportsService?.getAll(organizationId);
    return imports?.find(imp => imp?.id === importId);
  }

  // Get statement import errors
  async getStatementImportErrors(importId) {
    return await statementImportsService?.getErrors(importId);
  }

  // Delete statement import
  async deleteStatementImport(importId) {
    return await statementImportsService?.delete(importId);
  }

  // Get import statistics
  async getImportStats() {
    const { organizationId } = await this.getCurrentUserOrganization();
    return await statementImportsService?.getStats(organizationId);
  }

  // Download imported file
  async downloadStatementFile(filePath) {
    return await statementImportsService?.getFileUrl(filePath);
  }

  // Get predefined transaction categories
  async getTransactionCategories() {
    try {
      const { data, error } = await supabase?.from('transaction_categories')?.select('*')?.eq('is_system', true)?.order('category_group', { ascending: true })?.order('category_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching transaction categories:', error);
      throw error;
    }
  }

  // Get category mappings for organization
  async getCategoryMappings(organizationId) {
    try {
      const { data, error } = await supabase?.from('transaction_category_mappings')?.select(`
          *,
          account:chart_of_accounts(
            id,
            account_name,
            account_code,
            account_type
          )
        `)?.eq('organization_id', organizationId)?.eq('is_active', true)?.order('category_name', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching category mappings:', error);
      throw error;
    }
  }

  // Create category mapping
  async createCategoryMapping(mappingData) {
    try {
      const { data, error } = await supabase?.from('transaction_category_mappings')?.insert([mappingData])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating category mapping:', error);
      throw error;
    }
  }

  // Update category mapping
  async updateCategoryMapping(organizationId, categoryName, accountId) {
    try {
      // First check if the mapping exists
      const { data: existingMapping, error: checkError } = await supabase
        ?.from('transaction_category_mappings')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.eq('category_name', categoryName)
        ?.maybeSingle(); // Use maybeSingle instead of single to avoid error if no rows

      if (checkError) {
        console.error('Error checking existing category mapping:', checkError);
        throw checkError;
      }

      if (existingMapping) {
        // Update existing mapping
        const { data, error } = await supabase
          ?.from('transaction_category_mappings')
          ?.update({ 
            account_id: accountId,
            updated_at: new Date()?.toISOString()
          })
          ?.eq('organization_id', organizationId)
          ?.eq('category_name', categoryName)
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      } else {
        // Create new mapping if it doesn't exist
        const { user } = await this.getCurrentUserOrganization();
        
        const { data, error } = await supabase
          ?.from('transaction_category_mappings')
          ?.insert({
            organization_id: organizationId,
            category_name: categoryName,
            account_id: accountId,
            created_by: user?.id,
            is_active: true
          })
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error updating category mapping:', error);
      throw error;
    }
  }

  // Delete category mapping
  async deleteCategoryMapping(mappingId) {
    try {
      const { error } = await supabase?.from('transaction_category_mappings')?.delete()?.eq('id', mappingId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting category mapping:', error);
      throw error;
    }
  }

  // Auto-categorize transaction using AI - Enhanced with proper field handling
  async autoCategorizeTransaction(transactionId) {
    try {
      // Get transaction details
      const { data: transaction, error: fetchError } = await supabase?.from('bank_transactions')?.select('description, amount, organization_id')?.eq('id', transactionId)?.single();

      if (fetchError) throw fetchError;

      // Call the auto-categorization function
      const { data, error } = await supabase?.rpc('auto_categorize_transaction', {
        transaction_description: transaction?.description,
        transaction_amount: transaction?.amount,
        org_uuid: transaction?.organization_id
      });

      if (error) throw error;

      if (data && data?.length > 0) {
        const result = data?.[0];
        
        // Update transaction with AI suggestion - using proper field names
        await this.updateTransaction(transactionId, {
          category: result?.suggested_category,
          categoryConfidence: result?.confidence_score,
          accountId: result?.account_suggestion,
          aiSuggested: true
        });

        return {
          category: result?.suggested_category,
          confidence: result?.confidence_score,
          accountId: result?.account_suggestion
        };
      }

      return null;
    } catch (error) {
      console.error('Error auto-categorizing transaction:', error);
      throw error;
    }
  }

  // Bulk auto-categorize transactions
  async bulkAutoCategorizeTransactions(transactionIds) {
    try {
      const results = [];
      
      // Process transactions in batches to avoid overwhelming the system
      for (let i = 0; i < transactionIds?.length; i += 5) {
        const batch = transactionIds?.slice(i, i + 5);
        const batchPromises = batch?.map(id => this.autoCategorizeTransaction(id));
        const batchResults = await Promise.allSettled(batchPromises);
        results?.push(...batchResults);
      }

      // Count successful categorizations
      const successfulCount = results?.filter(result => 
        result?.status === 'fulfilled' && result?.value
      )?.length;

      return {
        totalProcessed: transactionIds?.length,
        successfulCount,
        results
      };
    } catch (error) {
      console.error('Error bulk auto-categorizing transactions:', error);
      throw error;
    }
  }

  // Create default category mappings for organization
  async createDefaultCategoryMappings(organizationId) {
    try {
      const { data, error } = await supabase?.rpc('create_default_category_mappings', {
        org_uuid: organizationId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating default category mappings:', error);
      throw error;
    }
  }

  // Get categorization analytics
  async getCategorizationAnalytics(organizationId, dateFrom, dateTo) {
    try {
      const { data, error } = await supabase?.from('bank_transactions')?.select('category, amount, category_confidence, categorized_by')?.eq('organization_id', organizationId)?.gte('transaction_date', dateFrom || '2024-01-01')?.lte('transaction_date', dateTo || new Date()?.toISOString()?.split('T')?.[0]);

      if (error) throw error;

      // Process analytics data
      const analytics = {
        totalTransactions: data?.length,
        categorizedCount: data?.filter(t => t?.category)?.length,
        uncategorizedCount: data?.filter(t => !t?.category)?.length,
        aiCategorizedCount: data?.filter(t => t?.category_confidence)?.length,
        averageConfidence: 0,
        categoryBreakdown: {},
        confidenceDistribution: { high: 0, medium: 0, low: 0 }
      };

      // Calculate average confidence
      const confidenceValues = data?.filter(t => t?.category_confidence)?.map(t => t?.category_confidence);
      if (confidenceValues?.length > 0) {
        analytics.averageConfidence = confidenceValues?.reduce((a, b) => a + b, 0) / confidenceValues?.length;
      }

      // Category breakdown
      data?.forEach(transaction => {
        if (transaction?.category) {
          analytics.categoryBreakdown[transaction.category] = 
            (analytics?.categoryBreakdown?.[transaction?.category] || 0) + Math.abs(transaction?.amount);
        }
      });

      // Confidence distribution
      data?.forEach(transaction => {
        if (transaction?.category_confidence) {
          const confidence = transaction?.category_confidence;
          if (confidence >= 0.8) analytics.confidenceDistribution.high++;
          else if (confidence >= 0.6) analytics.confidenceDistribution.medium++;
          else analytics.confidenceDistribution.low++;
        }
      });

      return analytics;
    } catch (error) {
      console.error('Error fetching categorization analytics:', error);
      throw error;
    }
  }

  // Get accounts grouped by account type for category selection
  async getAccountsByType() {
    try {
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data, error } = await supabase?.rpc('get_account_hierarchy', {
        org_uuid: organizationId
      });

      if (error) throw error;

      // Group accounts by account type
      const accountsByType = {};
      
      data?.forEach(account => {
        const accountType = account?.account_type;
        if (!accountsByType?.[accountType]) {
          accountsByType[accountType] = [];
        }
        
        accountsByType?.[accountType]?.push({
          id: account?.id,
          code: account?.account_code,
          name: account?.account_name,
          subtype: account?.account_subtype,
          path: account?.path,
          balance: account?.current_balance
        });
      });

      return accountsByType;
    } catch (error) {
      console.error('Error fetching accounts by type:', error);
      throw error;
    }
  }

  // Get enhanced category options with account types and account names
  async getEnhancedCategoryOptions() {
    try {
      const accountsByType = await this.getAccountsByType();
      const categoryOptions = [];

      // Add static categories first
      categoryOptions?.push(
        { value: '', label: 'Select Category', isHeader: false },
        { value: 'customer_payment', label: 'Customer Payment', isHeader: false },
        { value: 'customer_advance', label: 'Customer Advance', isHeader: false },
        { value: 'vendor_payment', label: 'Vendor Payment', isHeader: false },
        { value: 'vendor_advance', label: 'Vendor Advance', isHeader: false },
        { value: 'transfer_to_bank', label: 'Transfer to Another Bank', isHeader: false }
      );

      // Add account type based categories with proper hierarchical structure
      Object.keys(accountsByType)?.forEach(accountType => {
        // Add account type as header with proper formatting
        const typeLabel = accountType?.charAt(0)?.toUpperCase() + accountType?.slice(1)?.replace('_', ' ');
        categoryOptions?.push({
          value: `header_${accountType}`,
          label: `── ${typeLabel} Accounts ──`,
          isHeader: true,
          disabled: true,
          accountType: accountType
        });

        // Add accounts under this type with proper indentation and formatting
        accountsByType?.[accountType]?.forEach(account => {
          categoryOptions?.push({
            value: account?.name, // Use account name as the category value
            label: `${account?.code} - ${account?.name}`,
            isHeader: false,
            accountType: accountType,
            accountId: account?.id,
            accountName: account?.name,
            accountCode: account?.code,
            group: typeLabel // Group by account type for better organization
          });
        });
      });

      // Add other static categories at the end
      categoryOptions?.push(
        { value: 'header_other', label: '── Other Categories ──', isHeader: true, disabled: true },
        { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
        { value: 'Marketing & Advertising', label: 'Marketing & Advertising', isHeader: false },
        { value: 'Travel & Entertainment', label: 'Travel & Entertainment', isHeader: false },
        { value: 'Utilities', label: 'Utilities', isHeader: false },
        { value: 'Professional Services', label: 'Professional Services', isHeader: false },
        { value: 'Equipment & Software', label: 'Equipment & Software', isHeader: false },
        { value: 'Rent & Facilities', label: 'Rent & Facilities', isHeader: false },
        { value: 'Bank Fees', label: 'Bank Fees', isHeader: false },
        { value: 'Other', label: 'Other', isHeader: false }
      );

      return categoryOptions;
    } catch (error) {
      console.error('Error getting enhanced category options:', error);
      // Fallback to basic categories if there's an error
      return [
        { value: '', label: 'Select Category', isHeader: false },
        { value: 'customer_payment', label: 'Customer Payment', isHeader: false },
        { value: 'customer_advance', label: 'Customer Advance', isHeader: false },
        { value: 'vendor_payment', label: 'Vendor Payment', isHeader: false },
        { value: 'vendor_advance', label: 'Vendor Advance', isHeader: false },
        { value: 'transfer_to_bank', label: 'Transfer to Another Bank', isHeader: false },
        { value: 'Revenue', label: 'Revenue', isHeader: false },
        { value: 'Office Supplies', label: 'Office Supplies', isHeader: false },
        { value: 'Other', label: 'Other', isHeader: false }
      ];
    }
  }

  // Get account category display for showing in transaction table
  async getCategoryDisplayInfo(category) {
    try {
      if (!category) return null;

      // Check if it's an account-based category by trying to find it in chart of accounts
      const { organizationId } = await this.getCurrentUserOrganization();
      
      const { data: account, error } = await supabase?.from('chart_of_accounts')?.select(`
          id,
          account_code,
          account_name,
          account_type,
          account_subtype
        `)?.eq('organization_id', organizationId)?.eq('account_name', category)?.single();

      if (!error && account) {
        // Return account-based category info with type information
        return {
          id: account?.id,
          name: account?.account_name,
          code: account?.account_code,
          type: account?.account_type,
          subtype: account?.account_subtype,
          displayLabel: `${account?.account_code} - ${account?.account_name}`,
          isAccountBased: true,
          icon: this.getAccountTypeIcon(account?.account_type),
          color: this.getAccountTypeColor(account?.account_type)
        };
      }

      // Return static category info for non-account categories
      return {
        name: category,
        displayLabel: category,
        isAccountBased: false,
        icon: '📋',
        color: 'text-gray-600'
      };
    } catch (error) {
      console.error('Error getting category display info:', error);
      return {
        name: category,
        displayLabel: category,
        isAccountBased: false,
        icon: '📋',
        color: 'text-gray-600'
      };
    }
  }

  // Helper method to get account type icons
  getAccountTypeIcon(accountType) {
    const icons = {
      'asset': '💰',
      'liability': '📋',
      'equity': '🏛️',
      'revenue': '📈',
      'expense': '📉'
    };
    return icons?.[accountType] || '📁';
  }

  // Helper method to get account type colors
  getAccountTypeColor(accountType) {
    const colors = {
      'asset': 'text-green-600',
      'liability': 'text-red-600',
      'equity': 'text-blue-600',
      'revenue': 'text-emerald-600',
      'expense': 'text-orange-600'
    };
    return colors?.[accountType] || 'text-gray-600';
  }

  // Helper method to get static category icons
  getStaticCategoryIcon(category) {
    const icons = {
      'customer_payment': '👤',
      'customer_advance': '💰',
      'vendor_payment': '🏢',
      'vendor_advance': '💳',
      'transfer_to_bank': '🔄',
      'Office Supplies': '📎',
      'Marketing & Advertising': '📢',
      'Travel & Entertainment': '✈️',
      'Utilities': '💡',
      'Professional Services': '👔',
      'Equipment & Software': '🔧',
      'Rent & Facilities': '🏢',
      'Bank Fees': '🏦',
      'Other': '❓'
    };
    return icons?.[category] || '📋';
  }
}

export const bankingService = new BankingService();